import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { OrderNotesTypes } from '../types';
import { getOrder } from '../services';
import {
  deleteUpdateOrderNote,
  mutationCreateOrderNote,
  mutationUpdateOrderNote,
} from '../services/mutations';

const useOrderNotes = () => {
  const { id: orderId } = useParams<{ id: string }>();
  const [notesOrder, setNotes] = useState<OrderNotesTypes[]>();
  const [openModal, setOpenModal] = useState<{ open: boolean; note: OrderNotesTypes | null }>({
    open: false,
    note: null,
  });

  const [openDelete, setOpenDelete] = useState<{ status: boolean; note: string | null }>({
    status: false,
    note: null,
  });

  const getScore = (item: { critical: boolean; resolved: boolean }) => {
    if (item.critical && !item.resolved) return 0;
    if (!item.critical) return 1;
    return 2;
  };

  const filterNotes = (notes) => {
    return [...notes].sort((a, b) => {
      const aScore = getScore(a);
      const bScore = getScore(b);

      if (aScore !== bScore) return aScore - bScore;

      return new Date(b.created).getTime() - new Date(a.created).getTime();
    });
  };

  const { isLoading: isNotesLoading } = useQuery(['order-notes', orderId], () => getOrder(orderId), {
    enabled: !!orderId,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data?.getOrder?.notes) {
        const { notes } = data.getOrder;
        setNotes(filterNotes(notes));
      }
    },
  });

  const { mutate: createNoteMutation, ...responseCreateNoteMutation } = useMutation(
    mutationCreateOrderNote,
    {
      onSuccess: (data) => {
        const { notes } = data.createOrderNote;
        setNotes(filterNotes(notes));
        setOpenModal({ open: false, note: null });
        responseCreateNoteMutation.reset();
      },
    }
  );

  const { mutate: updateNoteMutation, ...responseUpdateNoteMutation } = useMutation(
    mutationUpdateOrderNote,
    {
      onSuccess: (data) => {
        const { notes } = data.updateOrderNote;
        setNotes(filterNotes(notes));
        setOpenModal({ open: false, note: null });
        responseUpdateNoteMutation.reset();
      },
    }
  );

  const { mutate: deleteNoteMutation, ...responseDeleteNoteMutation } = useMutation(
    deleteUpdateOrderNote,
    {
      onSuccess: (data) => {
        const { notes } = data.deleteOrderNote;
        setNotes(filterNotes(notes));
        setOpenDelete({ status: false, note: null });
        responseDeleteNoteMutation.reset();
      },
    }
  );

  const handleDeleteNote = (noteId: string) => {
    deleteNoteMutation({
      id: orderId,
      noteId,
    });
  };

  const handleUpdateNote = (
    content: string,
    critical: boolean,
    noteId: string,
    resolved: boolean
  ) => {
    updateNoteMutation({
      id: orderId,
      critical,
      content,
      noteId,
      resolved,
    });
  };

  const handleCreateNote = (content: string, critical: boolean) => {
    createNoteMutation({
      id: orderId,
      critical,
      content,
    });
  };

  const handleToogleOpen = (note = null) => {
    setOpenModal({
      open: !openModal.open,
      note,
    });
  };

  const handleToogleDelete = (note = null) => {
    setOpenDelete({
      status: !openDelete.status,
      note,
    });
  };

  return {
    notesOrder,
    openModal,
    openDelete,
    handleToogleDelete,
    handleToogleOpen,
    handleCreateNote,
    handleUpdateNote,
    handleDeleteNote,
    isNotesLoading,
    createNoteLoading: responseCreateNoteMutation.isLoading,
    updateNoteLoading: responseUpdateNoteMutation.isLoading,
    deleteNoteLoading: responseDeleteNoteMutation.isLoading,
  };
};

export default useOrderNotes;
